/**
 * Plan price rules (pure) — mirrors the backend (subscriptions/pricing.js):
 * billing currency = their own when Eaze can charge it, else USD when it
 * can, else GHS; a set price in that currency is what they pay, otherwise
 * the GHS price with an estimate in their money.
 */
import { currencyInfo, formatAmount } from "@/lib/currency";
import type { BillingCycle, LaunchPlan } from "@/constants/plans";

export interface ResolvedPrice {
  /** What they're charged (major units). */
  amount: number;
  currency: string;
  /** Charged in their own currency (no conversion by their bank). */
  exact: boolean;
  /** "≈ ₦45,000" — their own money, when charged in another currency. */
  estimate: string | null;
  /** Same estimate for another amount in the charged currency (a total). */
  estimateOf: (chargedMajor: number) => string | null;
}

/** "Ghana cedis", "US dollars". */
export const currencyPlural = (code: string) => {
  const n = currencyInfo(code).name;
  return n && n !== code ? `${n}s` : code;
};

export function billingCurrencyFor(local: string, chargeable: string[]): string {
  if (chargeable.includes(local)) return local;
  if (chargeable.includes("USD")) return "USD";
  return "GHS";
}

/** Round an estimate the way prices are quoted (₦45,000 not ₦44,873.21). */
export function roundEstimate(major: number): number {
  if (major >= 1000) {
    const step = major >= 100000 ? 1000 : major >= 10000 ? 100 : 10;
    return Math.round(major / step) * step;
  }
  return Math.round(major);
}

/** What a price needs from a plan. */
export type PricedPlan = Pick<LaunchPlan, "monthly" | "yearly" | "prices">;

/** A plan's set price in one currency (GHS = the base list), or null. */
export function planPriceIn(plan: PricedPlan, cycle: BillingCycle, currency: string): number | null {
  if (currency === "GHS") return cycle === "monthly" ? plan.monthly : plan.yearly;
  const v = plan.prices?.[currency]?.[cycle];
  return v != null && v > 0 ? v : null;
}

export function resolvePlanPrice(plan: PricedPlan, cycle: BillingCycle, local: string, chargeable: string[], ghsRate: number | null): ResolvedPrice {
  const ghs = cycle === "monthly" ? plan.monthly : plan.yearly;
  const billing = billingCurrencyFor(local, chargeable);
  const set = billing === "GHS" ? ghs : plan.prices?.[billing]?.[cycle] ?? null;
  const est = (major: number) => `≈ ${formatAmount(roundEstimate(major), local, { whole: true })}`;
  const make = (amount: number, currency: string): ResolvedPrice => {
    // Their money per unit charged, from the GHS list and today's rate.
    const perUnit = currency !== local && ghsRate && amount > 0 ? (ghs * ghsRate) / amount : null;
    const estimateOf = (m: number) => (perUnit ? est(m * perUnit) : null);
    return { amount, currency, exact: currency === local, estimate: estimateOf(amount), estimateOf };
  };
  return set != null && set > 0 ? make(set, billing) : make(ghs, "GHS");
}

export function formatPlanPrice(p: ResolvedPrice): string {
  return formatAmount(p.amount, p.currency, { whole: p.amount >= 100 && Number.isInteger(p.amount) });
}

